import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/helpers/ui-utils';
import { Slot, Slottable } from '@radix-ui/react-slot';

const badgeVariants = cva(
  [
    'outline-hidden',
    'inline-flex',
    'items-center',
    'border',
    'border-solid',
    'text-sm',
    'font-nunito-sans',
    'transition-all',
    'duration-300',
    'ease-in',
    'focus:outline-hidden',
  ],
  {
    variants: {
      variant: {
        default: [
          'border-primary',
          'bg-primary',
          'text-primary-foreground',
          'hover:bg-primary-hover',
          'hover:border-primary-hover',
          'focus:border-primary-hover',
        ],
        'primary-muted': [
          'border-[#FFF4F0]',
          'bg-[#FFF4F0]',
          'text-primary',
          'hover:bg-[#FFEAE0]',
          'hover:border-[#FFEAE0]',
          'focus:border-[#FFEAE0]',
        ],
        secondary: [
          'border-secondary',
          'bg-secondary',
          'text-secondary-foreground',
          'hover:bg-secondary-hover',
          'hover:border-secondary-hover',
          'focus:border-secondary-hover',
        ],
        tertiary: [
          'border-tertiary',
          'bg-tertiary',
          'text-tertiary-foreground',
          'hover:bg-tertiary-hover',
          'hover:border-tertiary-hover',
          'focus:border-tertiary-hover',
        ],
        success: [
          'border-[#22C55E]',
          'bg-[#22C55E]',
          'text-background',
          'hover:bg-[#16A34A]',
          'hover:border-[#16A34A]',
          'focus:border-[#16A34A]',
        ],
        warning: [
          'border-warning',
          'bg-warning',
          'text-warning-foreground',
          'hover:bg-warning-hover',
          'hover:border-warning-hover',
          'focus:border-warning-hover',
        ],
        destructive: [
          'border-destructive',
          'bg-destructive',
          'text-destructive-foreground',
          'hover:bg-destructive-hover',
          'hover:border-destructive-hover',
          'focus:border-destructive-hover',
        ],
        outline: [
          'bg-transparent',
          'border-primary',
          'text-primary',
          'hover:bg-[#FFF4F0]',
          'hover:border-primary-hover',
          'focus:border-primary-hover',
        ],
        black: [
          'border-foreground',
          'bg-foreground',
          'text-background',
          'hover:bg-[#404040]',
          'hover:border-[#404040]',
          'focus:border-[#404040]',
        ],
        light: [
          'border-transparent',
          'bg-white',
          'text-foreground',
          'shadow-sm',
          'hover:bg-white/90',
        ],
      },
      // Numbered leading-N classes are spacing-scale values in this Tailwind
      // version (leading-1 = 1 * --spacing = 4px), not a line-height
      // multiplier — using them for line-height here collapsed the badge's
      // box height regardless of padding. Use leading-tight/leading-[Npx]
      // instead.
      size: {
        default: ['px-2', 'py-1', 'leading-tight'],
        sm: ['px-[6px]', 'py-[2px]', 'text-xs', 'font-[300]', 'leading-5'],
        lg: ['px-3', 'py-[6px]', 'leading-tight'],
        overlay: [
          'px-3',
          'py-[6px]',
          'text-xs',
          'font-nunito-sans-bold',
          'uppercase',
          'tracking-wider',
          'leading-[16px]',
        ],
      },
      rounded: {
        default: 'rounded-full',
        xs: 'rounded-xs',
        sm: 'rounded-[4px]',
      },
      disabled: {
        true: ['opacity-50', 'hover:cursor-not-allowed'],
      },
      prefix: {
        true: ['items-center', 'flex', 'flex-row', 'gap-2'],
      },
      suffix: {
        true: ['items-center', 'flex', 'flex-row', 'gap-2'],
      },
    },
    compoundVariants: [
      {
        prefix: true,
        size: 'sm',
        className: 'gap-1',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      rounded: 'default',
    },
  },
);

type BadgeCVAProps = {
  /**
   * Visual style variant of the badge. Determines color scheme and emphasis level.
   * - `default` - Primary orange badge with white text
   * - `primary-muted` - Light orange background with orange text
   * - `secondary` - Secondary color scheme
   * - `tertiary` - Tertiary color scheme with border
   * - `success` - Green badge for success states
   * - `warning` - Warning color scheme for alerts
   * - `destructive` - Red badge for errors or dangerous actions
   * - `outline` - Transparent background with primary border
   * - `black` - Black background with white text
   * - `light` - White background with dark text and a soft shadow, for overlaying on photos
   * @default 'default'
   */
  variant?: VariantProps<typeof badgeVariants>['variant'];

  /**
   * Size variant controlling padding and text size.
   * - `default` - Standard badge size
   * - `sm` - Small badge with reduced padding and text
   * - `lg` - Large badge with increased padding
   * - `overlay` - Uppercase, letter-spaced status label, for overlaying on photos
   * @default 'default'
   */
  size?: VariantProps<typeof badgeVariants>['size'];

  /**
   * Border radius variant. Controls corner rounding.
   * - `default` - Fully rounded (pill shape)
   * - `xs` - Extra small rounding for more rectangular appearance
   * - `sm` - 4px rounding, for a mostly-square status label
   * @default 'default'
   */
  rounded?: VariantProps<typeof badgeVariants>['rounded'];

  /**
   * Indicates if the badge is in a disabled state. Applies visual disabled styling.
   * @default false
   */
  disabled?: VariantProps<typeof badgeVariants>['disabled'];
};

export interface BadgeProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, 'prefix' | 'suffix'>,
    BadgeCVAProps {
  /**
   * Render as child component using Radix Slot. Allows composition with other elements while maintaining badge styling.
   * @default false
   */
  asChild?: boolean;

  /**
   * React node to display before badge content. Typically small icons or indicators positioned on the left.
   */
  prefix?: React.ReactNode;

  /**
   * React node to display after badge content. Typically small icons or action indicators positioned on the right.
   */
  suffix?: React.ReactNode;
}

/**
 * Highlight important information with compact, styled labels that communicate status, categories, or counts at a glance.
 *
 * @param props - Badge properties. See {@link BadgeProps} for detailed prop documentation including inherited CVA variants.
 * @param ref - Forwarded ref to the badge element
 * @returns Rendered badge component
 *
 * @example
 * ```tsx
 * // Basic badge
 * <Badge>New</Badge>
 *
 * // Success badge
 * <Badge variant="success">Completed</Badge>
 *
 * // Badge with prefix icon
 * <Badge prefix={<CheckIcon />}>Verified</Badge>
 *
 * // Small warning badge
 * <Badge variant="warning" size="sm">Alert</Badge>
 *
 * // Outline badge with suffix
 * <Badge variant="outline" suffix={<ArrowIcon />}>View More</Badge>
 * ```
 */
const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant,
      size = 'default',
      rounded,
      disabled,
      prefix: prefixComponent,
      suffix: suffixComponent,
      asChild,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'span';

    const prefixSuffixSizes = cn(
      'flex',
      '[&_svg]:size-4',
      '[&_svg]:pointer-events-none',
      '[&_svg]:shrink-0',
      '[&_svg]:stroke-1',
      {
        'h-4 w-4': size === 'default' || size === 'lg',
        'h-3 w-3 [&_svg]:size-3': size === 'sm',
      },
    );

    const variantClass = badgeVariants({
      variant,
      size,
      rounded,
      disabled,
      prefix: Boolean(prefixComponent),
      suffix: Boolean(suffixComponent),
      className,
    });

    return (
      <Comp
        ref={ref}
        role="status"
        tabIndex={0}
        className={variantClass}
        {...props}
      >
        {prefixComponent && (
          <span className={cn('prefix', prefixSuffixSizes)}>
            {prefixComponent}
          </span>
        )}

        {React.isValidElement(children) && asChild ? (
          <Slottable>
            {React.cloneElement(children as React.ReactElement<HTMLElement>, {
              className: cn('children-content', variantClass),
            })}
          </Slottable>
        ) : (
          <span className="children-content">{children}</span>
        )}

        {suffixComponent && (
          <span className={cn('suffix', prefixSuffixSizes)}>
            {suffixComponent}
          </span>
        )}
      </Comp>
    );
  },
);

export default Badge;
